/**
 * StagingInbox — "Pending Imports" tab in Document Hub.
 *
 * Layout:
 *   ┌─ Analytics Cards ──────────────────────────────────────┐
 *   │  Total  │  Pending  │  Posted  │  Rejected             │
 *   │  Financial  │  Purchase  │  Sales  │  Inventory        │
 *   └────────────────────────────────────────────────────────┘
 *   ┌─ Tab Bar ──────────────────────────────────────────────┐
 *   │  All Imports  │  Logs                                  │
 *   └────────────────────────────────────────────────────────┘
 *   ┌─ Content ──────────────────────────────────────────────┐
 *   │  Imports list / Activity log                           │
 *   └────────────────────────────────────────────────────────┘
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Clock, CheckCircle2, XCircle, RefreshCw, Landmark, ShoppingCart,
  Receipt, Boxes, Activity, List, AlertCircle,
} from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;


const STATUS_STYLE = {
  Pending:  { color: '#f59e0b', icon: Clock,         bg: 'rgba(245,158,11,0.12)' },
  Posted:   { color: '#22c55e', icon: CheckCircle2,  bg: 'rgba(34,197,94,0.12)'  },
  Rejected: { color: '#ef4444', icon: XCircle,       bg: 'rgba(239,68,68,0.12)'  },
};

// Maps DocType → module for analytics breakdown
const DOC_MODULE = {
  journal_voucher:    'financial', opening_balance:  'financial', gl_account:     'financial',
  chart_of_accounts:  'financial', brs:              'financial', pdc:            'financial',
  purchase_order:     'purchase',  grn:              'purchase',  purchase_invoice: 'purchase',
  sales_invoice:      'sales',     sales_return:     'sales',
  stock_transfer:     'inventory', goods_issue:      'inventory',
};

const MODULE_META = {
  financial: { label: 'Financial', icon: Landmark,     color: '#3b82f6' },
  purchase:  { label: 'Purchase',  icon: ShoppingCart, color: '#f59e0b' },
  sales:     { label: 'Sales',     icon: Receipt,      color: '#10b981' },
  inventory: { label: 'Inventory', icon: Boxes,        color: '#8b5cf6' },
};

export default function StagingInbox({ isDark, border, bgCard, text, textMut, onOpen }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setFilter] = useState('');
  const [activeTab, setTab]       = useState('imports'); // 'imports' | 'logs'

  const load = useCallback(() => {
    setLoading(true);
    // Always fetch all for analytics, filter client-side for the list
    fetch(`${API}/api/document-hub/staging`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => setRows(d.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Analytics ── */
  const total    = rows.length;
  const pending  = rows.filter(r => r.Status === 'Pending').length;
  const posted   = rows.filter(r => r.Status === 'Posted').length;
  const rejected = rows.filter(r => r.Status === 'Rejected').length;

  const moduleStats = Object.entries(MODULE_META).map(([id, meta]) => ({
    id, ...meta,
    count: rows.filter(r => (DOC_MODULE[r.DocType] || 'other') === id).length,
    success: rows.filter(r => (DOC_MODULE[r.DocType] || 'other') === id && r.Status === 'Posted').length,
    failed:  rows.filter(r => (DOC_MODULE[r.DocType] || 'other') === id && r.Status === 'Rejected').length,
  }));

  /* ── Filtered rows for list ── */
  const filteredRows = statusFilter ? rows.filter(r => r.Status === statusFilter) : rows;

  /* ── Styles ── */
  const cardBg   = isDark ? '#1a1a1a' : '#ffffff';
  const divider  = isDark ? '#262626' : '#e2e8f0';

  return (
    <div>
      {/* ── Analytics: Status Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
        {[
          { label: 'Total',    value: total,    color: isDark ? '#e2e8f0' : '#1e293b', bg: isDark ? '#1a1a1a' : '#f8fafc', border: divider },
          { label: 'Pending',  value: pending,  color: '#f59e0b', bg: isDark ? '#1a0e00' : '#fffbeb', border: '#fcd34d40' },
          { label: 'Posted',   value: posted,   color: '#22c55e', bg: isDark ? '#001a0e' : '#f0fdf4', border: '#4ade8040' },
          { label: 'Rejected', value: rejected, color: '#ef4444', bg: isDark ? '#1a0000' : '#fef2f2', border: '#fca5a540' },
        ].map(s => (
          <div key={s.label} data-testid={`staging-stat-${s.label.toLowerCase()}`}
            style={{ padding: '12px 16px', borderRadius: 10, background: s.bg, border: `1px solid ${s.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: textMut, marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Analytics: Module Breakdown ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {moduleStats.map(m => {
          const MIcon = m.icon;
          return (
            <div key={m.id} data-testid={`staging-module-${m.id}`}
              style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${divider}`, background: cardBg, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${m.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MIcon size={16} color={m.color} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: text }}>{m.count}</div>
                <div style={{ fontSize: 10, color: textMut, fontWeight: 600 }}>{m.label}</div>
                {m.count > 0 && (
                  <div style={{ fontSize: 9, color: textMut, marginTop: 2 }}>
                    {m.success > 0 && <span style={{ color: '#22c55e' }}>{m.success} ok </span>}
                    {m.failed  > 0 && <span style={{ color: '#ef4444' }}>{m.failed} fail</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Tab Bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4, padding: '4px', background: isDark ? '#1a1a1a' : '#f1f5f9', borderRadius: 10 }}>
          {[
            { id: 'imports', label: 'All Imports', icon: List     },
            { id: 'logs',    label: 'Logs',         icon: Activity },
          ].map(t => {
            const TIcon  = t.icon;
            const active = activeTab === t.id;
            return (
              <button key={t.id} data-testid={`staging-tab-${t.id}`} onClick={() => setTab(t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, border: 'none',
                  background: active ? (isDark ? '#262626' : '#ffffff') : 'transparent',
                  color: active ? text : textMut, fontSize: 12, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', boxShadow: active ? (isDark ? '0 1px 4px rgba(0,0,0,.4)' : '0 1px 3px rgba(0,0,0,.1)') : 'none' }}>
                <TIcon size={12} />{t.label}
              </button>
            );
          })}
        </div>

        {/* Filter + refresh (only shown for imports tab) */}
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab === 'imports' && (
            <select data-testid="staging-status-filter" value={statusFilter} onChange={e => setFilter(e.target.value)}
              style={{ fontSize: 11, padding: '5px 8px', borderRadius: 8, border: `1px solid ${divider}`, background: cardBg, color: text }}>
              <option value="">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Posted">Posted</option>
              <option value="Rejected">Rejected</option>
            </select>
          )}
          <button data-testid="staging-refresh-btn" onClick={load}
            style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${divider}`, background: 'transparent', color: textMut, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      {activeTab === 'imports' ? (
        <ImportsTable rows={filteredRows} loading={loading} isDark={isDark} border={divider} bgCard={cardBg} text={text} textMut={textMut} onOpen={onOpen} />
      ) : (
        <LogsTable rows={rows} loading={loading} isDark={isDark} border={divider} bgCard={cardBg} text={text} textMut={textMut} />
      )}
    </div>
  );
}

/* ── Imports List ── */
function ImportsTable({ rows, loading, isDark, border, bgCard, text, textMut, onOpen }) {
  if (loading) return (
    <div style={{ padding: 32, textAlign: 'center', color: textMut, fontSize: 12 }}>
      <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} color={textMut} />
      <div>Loading imports…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (rows.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <FileText size={36} color={textMut} style={{ marginBottom: 10, opacity: 0.5 }} />
      <div style={{ fontSize: 13, fontWeight: 600, color: textMut }}>No imports found</div>
      <div style={{ fontSize: 11, color: textMut, marginTop: 4 }}>Use "New Import" to bring in a document.</div>
    </div>
  );
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.9fr 1.1fr 0.7fr', gap: 8, padding: '8px 14px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
        {['Document', 'Type', 'Party', 'Ref No.', 'Status'].map(h => (
          <div key={h} style={{ fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</div>
        ))}
      </div>

      {rows.map((row, i) => {
        const st    = STATUS_STYLE[row.Status] || STATUS_STYLE.Pending;
        const StIcon = st.icon;
        const mod   = MODULE_META[DOC_MODULE[row.DocType]] || {};
        const ModIcon = mod.icon;
        return (
          <div key={row.Id} data-testid={`staging-row-${row.Id}`} onClick={() => onOpen(row.Id)}
            style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.9fr 1.1fr 0.7fr', gap: 8,
              alignItems: 'center', padding: '10px 14px',
              borderTop: i > 0 ? `1px solid ${border}` : 'none', cursor: 'pointer' }}
            onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = isDark ? '#1a1a1a' : '#f8fafc'; }}
            onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>

            {/* Document */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: '#8b5cf615', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={14} color="#8b5cf6" />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.FileName}</div>
                <div style={{ fontSize: 10, color: textMut }}>{new Date(row.CreatedDate).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</div>
              </div>
            </div>

            {/* Type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
              {ModIcon && <ModIcon size={11} color={mod.color || textMut} />}
              <span style={{ fontSize: 11, color: textMut, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.DocType.replace(/_/g, ' ')}
              </span>
            </div>

            {/* Party */}
            <div style={{ fontSize: 11, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.PartyName || <span style={{ color: textMut }}>—</span>}
            </div>

            {/* Ref No */}
            <div style={{ fontSize: 11, fontFamily: 'monospace', color: row.PostedRefNo ? '#8b5cf6' : textMut, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.PostedRefNo || '—'}
            </div>

            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, background: st.bg, width: 'fit-content' }}>
              <StIcon size={10} color={st.color} />
              <span style={{ fontSize: 10, fontWeight: 700, color: st.color }}>{row.Status}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Logs Table ── */
function LogsTable({ rows, loading, isDark, border, bgCard, text, textMut }) {
  if (loading) return (
    <div style={{ padding: 32, textAlign: 'center', color: textMut, fontSize: 12 }}>
      <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} color={textMut} />
      <div>Loading logs…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (rows.length === 0) return (
    <div style={{ padding: 32, textAlign: 'center', fontSize: 12, color: textMut }}>No log entries yet.</div>
  );

  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '60px 1.6fr 1fr 0.8fr 1fr 2fr', gap: 8, padding: '8px 14px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
        {['ID', 'File', 'Doc Type', 'Status', 'Timestamps', 'Notes'].map(h => (
          <div key={h} style={{ fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</div>
        ))}
      </div>

      {rows.map((row, i) => {
        const st    = STATUS_STYLE[row.Status] || STATUS_STYLE.Pending;
        const StIcon = st.icon;
        const hasError = !!row.ErrorMessage;
        return (
          <div key={row.Id}
            style={{ display: 'grid', gridTemplateColumns: '60px 1.6fr 1fr 0.8fr 1fr 2fr', gap: 8,
              alignItems: 'start', padding: '10px 14px',
              borderTop: i > 0 ? `1px solid ${border}` : 'none',
              background: hasError ? (isDark ? '#1a00001a' : '#fff5f5') : 'transparent' }}>

            {/* ID */}
            <div style={{ fontSize: 11, fontFamily: 'monospace', color: textMut }}>#{row.Id}</div>

            {/* File */}
            <div style={{ fontSize: 11, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{row.FileName}</div>

            {/* Doc Type */}
            <div style={{ fontSize: 11, color: textMut }}>{row.DocType.replace(/_/g, ' ')}</div>

            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <StIcon size={10} color={st.color} />
              <span style={{ fontSize: 10, fontWeight: 700, color: st.color }}>{row.Status}</span>
            </div>

            {/* Timestamps */}
            <div style={{ fontSize: 10, color: textMut, lineHeight: 1.6 }}>
              <div>Created: {new Date(row.CreatedDate).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</div>
              {row.PostedDate && <div>Posted: {new Date(row.PostedDate).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</div>}
            </div>

            {/* Notes: Posted ref OR error */}
            <div style={{ fontSize: 11, minWidth: 0 }}>
              {row.PostedRefNo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={11} color="#22c55e" />
                  <span style={{ fontFamily: 'monospace', color: '#22c55e', fontWeight: 700 }}>{row.PostedRefNo}</span>
                </div>
              )}
              {hasError && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                  <AlertCircle size={11} color="#ef4444" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ color: '#ef4444', fontSize: 10, wordBreak: 'break-word' }}>{row.ErrorMessage}</span>
                </div>
              )}
              {!row.PostedRefNo && !hasError && (
                <span style={{ color: textMut, fontSize: 10 }}>—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
